import * as React from 'react';
import { Popover, PopoverState } from 'react-tiny-popover';
import * as numeral from 'numeral';
const Switch = require('rc-switch').default;
import {
  parseBindPath,
  TemplateOptions,
  TemplateUI,
  TemplateUIBind,
  TemplateUIIntroType,
  TemplateUIType,
  TEMPLATE_UI_INTROS
} from '../data/FlyerData';
import { getStoreState, storeDispatch } from '../state/Store';
import * as Actions from '../state/actions/index';
import { TemplateUIFooterView } from './TemplateUIFooter';
import { TemplateUISoundtrackView } from './TemplateUISoundtrack';
import {
  CustomProperty,
  getPropertyPositionWithValue,
  getPropertyValueWithPosition
} from '../data/CustomProperty';
import {
  ColorTheme,
  COLOR_THEME_PROPERTIES,
  getMatchingTheme
} from 'data/ColorTheme';
import { ThemePicker } from './components/ThemePicker';
import { LibraryImagePicker } from './components/LibraryImagePicker';
import { ColorPicker } from './components/ColorPicker';
import { Frame } from 'data/Frame';
import { trackEvent } from 'utils/Tracker';
import { useMobileLayout } from 'hooks/useMobileLayout';
import { IosCloseButton } from 'components/iOSCloseButton';

// const getFirstBind = (data: TemplateUI): string => {
//   let bind: string | TemplateUIBind = Array.isArray(data.bind)
//     ? data.bind[0]
//     : data.bind;
//   if (typeof bind === 'string') {
//     return bind;
//   }
//   return bind.node;
// };

const getBindValue = (bind: string): any | null => {
  const { node, effect, propertyName } = parseBindPath(bind);
  if (node) {
    return effect ? effect.getProperty(propertyName) : node[propertyName];
  }
  return null;
};

// const getFirstValidValue = (data: TemplateUI): any | null => {
//   let binds = Array.isArray(data.bind) ? data.bind : [data.bind];
//   for (const bind in binds) {
//     const b = typeof bind === 'string' ? bind : (bind as TemplateUIBind).node;
//     const value = getBindValue(b);
//     if (value !== null) {
//       return value;
//     }
//   }
//   return null;
// };

const getFirstValidBind = (data: TemplateUI): string | null => {
  let binds = Array.isArray(data.bind) ? data.bind : [data.bind];
  for (const bind of binds) {
    const b = typeof bind === 'string' ? bind : (bind as TemplateUIBind).node;
    const value = getBindValue(b);
    if (value !== null) {
      return b;
    }
  }
  return null;
};

const getFormattedValue = (data: TemplateUI, value: any): string => {
  switch (data.type) {
    case TemplateUIType.Boolean:
    case TemplateUIType.Text:
    case TemplateUIType.TextArea:
    case TemplateUIType.Image:
      return '';
    case TemplateUIType.Int:
      return Math.round(value) + '';
    case TemplateUIType.Float:
      const format = '0.[000]';
      return numeral(value).format(format);
    case TemplateUIType.Color:
      return value.toString().toUpperCase();
    default:
      break;
  }
  return value.toString();
};

const dispatchChange = (property: TemplateUI, value: any) => {
  storeDispatch(Actions.changeTemplateProperty(property, value));
};

const dispatchInputTextBlur = (property: TemplateUI) => {
  storeDispatch(Actions.didBlurTemplateInputText(property));
};

const dispatchColorBlur = (property: TemplateUI) => {
  storeDispatch(Actions.didBlurTemplateInputText(property));
};

interface InputThemeProps {
  value: ColorTheme;
  onChange: (value: ColorTheme | null) => void;
  onBlur: () => void;
  forceOpenFunc: React.RefObject<Function>;
  onUpgrade: () => void;
}
const InputTheme: React.FunctionComponent<InputThemeProps> = props => {
  const { value, onChange, onUpgrade } = props;
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const popoverContentRef = React.useRef<HTMLDivElement>(null);
  const isMobile = useMobileLayout();
  React.useEffect(() => {
    (props.forceOpenFunc as any).current = forceOpen;
    return () => {
      (props.forceOpenFunc as any).current = null;
    };
  }, []);

  function forceOpen() {
    setIsOpen(true);
  }

  const didChange = React.useCallback(
    (theme: ColorTheme) => {
      onChange(theme);
      if (isMobile) {
        setIsOpen(false);
      }
    },
    [isMobile, onChange]
  );

  return (
    <div className="input color color--theme">
      <Popover
        isOpen={isOpen}
        padding={5}
        onClickOutside={() => {
          props.onBlur();
          setIsOpen(false);
        }}
        reposition={false}
        contentLocation={
          isMobile
            ? { top: 0, left: 0 }
            : (popoverState: PopoverState) => {
                let top = popoverState.childRect.bottom + popoverState.padding;
                const left = popoverState.childRect.left;
                if (popoverContentRef.current) {
                  const contentHeight =
                    popoverContentRef.current.getBoundingClientRect().height;
                  top = Math.min(
                    top,
                    popoverState.parentRect.bottom - contentHeight
                  );
                }
                return { top, left };
              }
        }
        content={
          <>
            <ThemePicker
              ref={popoverContentRef}
              selectedTheme={value}
              onChange={didChange}
              onUpgrade={() => {
                setIsOpen(false);
                onUpgrade();
              }}
            />
            {isMobile && <IosCloseButton onClick={() => setIsOpen(false)} />}
          </>
        }
      >
        <div
          className="swatch swatch-theme"
          style={{
            backgroundColor: value.background
          }}
          onClick={() => setIsOpen(!isOpen)}
        >
          <svg
            style={{ pointerEvents: 'none', width: '100%' }}
            width="256"
            height="18"
            viewBox="0 0 256 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
          >
            <path d="M0 18L256 0V18H0Z" fill={value.foreground} />
          </svg>
        </div>
      </Popover>
    </div>
  );
};

interface InputLibraryProps {
  imageUrl: string;
  libraryName: string | undefined;
  onSelect: (imageUrl: string) => void;
  onSelectOther: () => void;
}
const InputLibraryImage: React.FunctionComponent<InputLibraryProps> = props => {
  const { imageUrl, libraryName, onSelect, onSelectOther } = props;
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const popoverContentRef = React.useRef<HTMLDivElement>(null);
  const isMobile = useMobileLayout();

  const didSelect = React.useCallback(
    (url: string) => {
      onSelect(url);
      if (isMobile) {
        setIsOpen(false);
      }
    },
    [onSelect, isMobile]
  );

  return (
    <div className="input image">
      <Popover
        isOpen={isOpen}
        padding={1}
        onClickOutside={() => {
          // props.onBlur();
          setIsOpen(false);
        }}
        reposition={false}
        contentLocation={
          isMobile
            ? { top: 0, left: 0 }
            : (popoverState: PopoverState) => {
                // console.log('CONTENT LOCATION');
                // console.log(popoverState);
                let top = popoverState.childRect.bottom + popoverState.padding;
                const left = popoverState.childRect.left;
                if (popoverContentRef.current) {
                  const contentHeight =
                    popoverContentRef.current.getBoundingClientRect().height;
                  top = Math.min(
                    top,
                    popoverState.parentRect.bottom - contentHeight
                  );
                }
                return { top, left };
              }
        }
        content={
          libraryName ? (
            <>
              <LibraryImagePicker
                ref={popoverContentRef}
                category={libraryName}
                selectedImageUrl={imageUrl}
                onSelect={didSelect}
                onSelectOther={() => {
                  setIsOpen(false);
                  onSelectOther();
                }}
              />
              {isMobile && <IosCloseButton onClick={() => setIsOpen(false)} />}
            </>
          ) : (
            <div />
          )
        }
      >
        <img
          src={imageUrl}
          // crossOrigin="anonymous"
          onClick={() => {
            if (libraryName) {
              setIsOpen(true);
            } else {
              onSelectOther();
            }
          }}
        />
      </Popover>
    </div>
  );
};

interface UseColorThemeButtonProps {
  onSelect: (value: ColorTheme) => void;
  onUpgrade: () => void;
}
const UseColorThemeButton: React.FunctionComponent<
  UseColorThemeButtonProps
> = props => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const popoverContentRef = React.useRef<HTMLDivElement>(null);
  const isMobile = useMobileLayout();

  const didChange = React.useCallback(
    (theme: ColorTheme) => {
      props.onSelect(theme);
      if (isMobile) {
        setIsOpen(false);
      }
    },
    [isMobile, props.onSelect]
  );

  return (
    <Popover
      isOpen={isOpen}
      onClickOutside={() => {
        setIsOpen(false);
      }}
      reposition={false}
      contentLocation={
        isMobile
          ? { top: 0, left: 0 }
          : (popoverState: PopoverState) => {
              let top = popoverState.childRect.bottom + popoverState.padding;
              const left = popoverState.childRect.left;
              if (popoverContentRef.current) {
                const contentHeight =
                  popoverContentRef.current.getBoundingClientRect().height;
                top = Math.min(
                  top,
                  popoverState.parentRect.bottom - contentHeight
                );
              }
              return { top, left };
            }
      }
      content={
        <>
          <ThemePicker
            ref={popoverContentRef}
            selectedTheme={null}
            onChange={didChange}
            onUpgrade={() => {
              setIsOpen(false);
              props.onUpgrade();
            }}
          />
          {isMobile && <IosCloseButton onClick={() => setIsOpen(false)} />}
        </>
      }
      containerStyle={isMobile ? undefined : { marginLeft: '5px' }}
    >
      <button
        className="template-ui-options-button"
        onClick={() => setIsOpen(!isOpen)}
      >
        Use color theme
      </button>
    </Popover>
  );
};

/* TODO: the following could could be refactored.
Maybe using functional components and hooks could be cleaner.
Still annoying that a text input or area can't be controlled by a prop.
Maybe there is a mistake somewhere else.
*/
interface InputTextProps {
  property: TemplateUI;
  value: string;
}
interface InputTextState {
  text: string;
}
class InputText extends React.Component<InputTextProps, InputTextState> {
  state: InputTextState = {
    text: ''
  };
  componentDidMount() {
    this.setState({ text: this.props.value });
  }
  didChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ text: e.target.value });
    dispatchChange(this.props.property, e.target.value);
  };
  didBlur = () => {
    dispatchInputTextBlur(this.props.property);
  };
  render() {
    return (
      <input
        type="text"
        value={this.state.text}
        onChange={this.didChange}
        onBlur={this.didBlur}
      />
    );
  }
}
class InputTextArea extends React.Component<InputTextProps, InputTextState> {
  state: InputTextState = {
    text: ''
  };
  componentDidMount() {
    this.setState({ text: this.props.value });
  }
  didChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({ text: e.target.value });
    dispatchChange(this.props.property, e.target.value);
  };
  didBlur = () => {
    dispatchInputTextBlur(this.props.property);
  };
  render() {
    return (
      <textarea
        value={this.state.text}
        onChange={this.didChange}
        onBlur={this.didBlur}
      />
    );
  }
}

interface PropertyTypeProps {
  type: TemplateUIType;
  title: string;
  formattedValue: string;
}

const PropertyWrapper: React.FunctionComponent<PropertyTypeProps> = props => {
  return (
    <div className={`template-ui-item template-ui-item--${props.type}`}>
      <div className="template-ui-header">
        <span>{props.title}</span>
        <span className="template-ui-item-value">{props.formattedValue}</span>
      </div>
      {props.children}
    </div>
  );
};

interface Props {
  properties: TemplateUI[];
  options: TemplateOptions | null;
  detachColorTheme: boolean;
  introType: TemplateUIIntroType;
}

const getThemePropertyFromLabel = (label: string): string | null => {
  for (const name of COLOR_THEME_PROPERTIES) {
    if (label.toLowerCase().includes(name)) {
      return name;
    }
  }
  return null;
};

const getColorValue = (p: TemplateUI): string => {
  const bind = getFirstValidBind(p);
  if (!bind) {
    return '';
  }
  const { node, effect, propertyName } = parseBindPath(bind);
  if (node) {
    const value = effect
      ? effect.getProperty(propertyName)
      : node[propertyName];
    return value || '';
  } else {
    return '';
  }
};

const getVisibility = (bind: string): boolean => {
  const invert = bind.startsWith('!');
  if (invert) {
    bind = bind.substring(1);
  }
  const { node, effect, propertyName } = parseBindPath(bind);
  if (node === null) {
    return false;
  }
  let value = false;
  if (propertyName === 'visible') {
    if (node instanceof Frame) {
      console.error(`Frame doesn't have visible property`);
      value = false;
    } else {
      value = (effect ?? node).visible;
    }
  } else {
    value = (
      effect ? effect.getProperty(propertyName) : node[propertyName]
    ) as boolean;
  }
  if (invert) {
    return !value;
  }
  return value;
};

export const TemplateUIView: React.FunctionComponent<Props> = props => {
  const forceOpenThemePopover = React.useRef<Function>(null);
  const colorProperties = props.properties.filter(
    p =>
      p.type === TemplateUIType.Color &&
      (p.visible === undefined || getVisibility(p.visible))
  );
  let canUseTheme = colorProperties.length > 1;
  const usedTheme: ColorTheme = {
    accent: '',
    background: '',
    foreground: ''
  };
  if (canUseTheme) {
    for (const p of colorProperties) {
      const property = getThemePropertyFromLabel(p.label);
      if (property) {
        usedTheme[property] = getColorValue(p);
      } else {
        canUseTheme = false;
        break;
      }
    }
  }
  let selectedTheme: ColorTheme | null = null;
  if (canUseTheme && !props.detachColorTheme) {
    // check match
    selectedTheme = getMatchingTheme(
      usedTheme,
      getStoreState().app.user?.colorThemes ?? []
    );
  }

  const didChangeTheme = (theme: ColorTheme | null) => {
    if (theme) {
      colorProperties.forEach(p => {
        const property = getThemePropertyFromLabel(p.label);
        if (property) {
          const color = theme[property];
          dispatchChange(p, color);
        }
      });
      storeDispatch(Actions.themeDidChange(theme));
    } else {
      storeDispatch(Actions.detachColorTheme());
    }
  };

  const didBlurThemePanel = () => {
    colorProperties.forEach(p => dispatchColorBlur(p));
  };

  const openThemePopover = () => {
    setTimeout(() => {
      if (forceOpenThemePopover.current) {
        forceOpenThemePopover.current();
      } else {
        openThemePopover();
      }
    }, 1);
  };

  const showUpgradeModal = React.useCallback(() => {
    trackEvent('Clicked Upgrade', { location: 'Themes picker' });
    storeDispatch(Actions.showBuyingOptions());
  }, []);

  const isMobile = useMobileLayout();

  const renderColors = (): React.ReactElement => {
    return selectedTheme ? (
      <PropertyWrapper
        type={TemplateUIType.Select}
        title="Theme"
        formattedValue={`${selectedTheme.background} ${selectedTheme.foreground}`.toUpperCase()}
      >
        <InputTheme
          value={selectedTheme}
          onChange={didChangeTheme}
          onBlur={didBlurThemePanel}
          forceOpenFunc={forceOpenThemePopover}
          onUpgrade={showUpgradeModal}
        />
      </PropertyWrapper>
    ) : (
      <div className="template-ui-group">
        {colorProperties.map((p, index) => {
          const value = getColorValue(p);
          const formattedValue = getFormattedValue(p, value);
          return (
            <PropertyWrapper
              type={p.type}
              title={p.label}
              formattedValue={formattedValue}
              key={index}
            >
              <ColorPicker
                color={value}
                onChange={c => dispatchChange(p, c)}
                onBlur={() => dispatchColorBlur(p)}
                hideAlpha={true}
              />
            </PropertyWrapper>
          );
        })}
        <UseColorThemeButton
          onSelect={theme => {
            didChangeTheme(theme);
            if (!isMobile) {
              openThemePopover();
            }
          }}
          onUpgrade={showUpgradeModal}
        />
      </div>
    );
  };

  return (
    <>
      <div className="template-ui">
        {props.properties.map((p, index) => {
          if (p.visible !== undefined && !getVisibility(p.visible)) {
            return null;
          }
          if (canUseTheme && p.type === TemplateUIType.Color) {
            // renders colors or theme later
            return null;
          }
          let firstBind = getFirstValidBind(p);
          if (!firstBind) {
            return null;
          }
          const invert = firstBind.startsWith('!');
          if (invert) {
            firstBind = firstBind.substring(1);
          }
          const { node, effect, propertyName } = parseBindPath(firstBind);
          if (node) {
            let value = undefined;
            if (propertyName === 'visible') {
              if (node instanceof Frame) {
                console.error(`Frame doesn't have visible property`);
                value = false;
              } else {
                value = (effect ?? node).visible;
              }
            } else {
              value = effect
                ? effect.getProperty(propertyName)
                : node[propertyName];
            }
            if (invert) {
              value = !value;
            }

            const formattedValue = getFormattedValue(p, value);
            let control: any = null;
            switch (p.type) {
              case TemplateUIType.Boolean:
                control = (
                  <Switch
                    checked={value}
                    onChange={(b: boolean) => dispatchChange(p, b)}
                  />
                );
                break;
              case TemplateUIType.Text:
                control = <InputText property={p} value={value} />;
                break;
              case TemplateUIType.TextArea:
                control = <InputTextArea property={p} value={value} />;
                break;
              case TemplateUIType.Color:
                control = (
                  <ColorPicker
                    color={value}
                    onChange={c => dispatchChange(p, c)}
                    onBlur={() => dispatchColorBlur(p)}
                    hideAlpha={true}
                  />
                );
                break;
              case TemplateUIType.Image:
                control = (
                  <InputLibraryImage
                    imageUrl={value}
                    libraryName={p.library}
                    onSelect={imageUrl => {
                      storeDispatch(
                        Actions.changeTemplateProperty(p, imageUrl)
                      );
                    }}
                    onSelectOther={() => {
                      storeDispatch(Actions.showTemplateImageReplaceOptions(p));
                    }}
                  />
                );
                break;
              case TemplateUIType.Float:
                const customProperty = effect?.getPropertiesDescriptors()[
                  propertyName
                ] as CustomProperty;
                if (customProperty) {
                  control = (
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.0001}
                      value={getPropertyPositionWithValue(
                        customProperty,
                        value
                      )}
                      onChange={e => {
                        dispatchChange(
                          p,
                          getPropertyValueWithPosition(
                            customProperty,
                            parseFloat(e.target.value)
                          )
                        );
                      }}
                    />
                  );
                }

                break;
              default:
                break;
            }
            return (
              <PropertyWrapper
                type={p.type}
                title={p.label}
                formattedValue={formattedValue}
                key={index}
              >
                {control}
              </PropertyWrapper>
            );
          }
          return null;
        })}
        {!props.options?.isAnimated && (
          <PropertyWrapper
            type={TemplateUIType.Select}
            title="Intro Animation"
            formattedValue=""
          >
            <select
              onChange={e =>
                storeDispatch(
                  Actions.changeTemplateIntroAnimation(
                    e.target.value as TemplateUIIntroType
                  )
                )
              }
              value={props.introType}
              style={{ textTransform: 'capitalize' }}
            >
              {TEMPLATE_UI_INTROS.map(type => (
                <option value={type} key={type}>
                  {type}
                </option>
              ))}
            </select>
          </PropertyWrapper>
        )}
        {canUseTheme && renderColors()}
        <TemplateUISoundtrackView />
      </div>
      {!isMobile && <TemplateUIFooterView />}
    </>
  );
};



// WEBPACK FOOTER //
// ./src/controller/TemplateUIView.tsx